import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Observable, throwError } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";

import { AuthService } from "../services/auth.service";

interface AuthRequestHeaders {
	Authorization?: string;
}

/**
 * The AuthInterceptor retrieves the Authorization token from the OAuthService and adds it to the headers of the http request
 * it intercepted. The token is only added if user is authenticated and therefore the token was set in application state.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
	constructor(
		private readonly authService: AuthService,
		private readonly router: Router,
	) {}

	intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		const headers: AuthRequestHeaders = {};
		const token = this.authService.accessToken;
		if (token) {
			headers.Authorization = "Bearer " + token;
		}

		return next
			.handle(
				req.clone({
					setHeaders: {
						Authorization: token ? `Bearer ${token}` : `Bearer`,
					},
				}),
			)
			.pipe(
				catchError((error: HttpErrorResponse) => {
					if (error.status === 400 && req.url.includes("refreshtoken")) {
						//initialized doesnt mean authenticated, but that initial authentication was tried
						//(through login or with refreshtoken)
						this.authService.isInitialized = true;
						this.authService.logout();
					}
					if (error.status === 401) {
						// Call refresh token endpoint
						return this.authService.refreshToken().pipe(
							switchMap(() => {
								// Resend the original request with the updated token
								const authToken = this.authService.accessToken;
								const authRequest = req.clone({
									setHeaders: {
										Authorization: `Bearer ${authToken}`,
									},
								});
								return next.handle(authRequest);
							}),
							catchError(() => {
								// Handle refresh token error (e.g., logout user)
								return throwError(error);
							}),
						);
					}
					// For other errors, simply pass the error through
					return throwError(error);
				}),
			);
	}
}
